/**
 * AdminClientsHub - Centro de gestión de clientes
 * Lista filtrable de todos los clientes (Premium/Free/Pendientes/Inactivos)
 * Mobile-first, carga rápida
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import ClientCard from "../../components/admin/ClientCard";
import ClientFilters from "../../components/admin/ClientFilters";
import { 
  Search,
  RefreshCw,
  Users,
  Crown,
  AlertCircle,
  ChevronLeft
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Skeleton loader for client cards
const ClientSkeleton = () => (
  <div className="p-4 rounded-xl bg-[#121212] border border-[#2A2A2A] animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-[#2A2A2A]" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-24 bg-[#2A2A2A] rounded" />
        <div className="h-3 w-16 bg-[#2A2A2A] rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-12 bg-[#2A2A2A] rounded" />
        <div className="h-3 w-16 bg-[#2A2A2A] rounded" />
      </div>
    </div>
  </div>
);

const AdminClientsHub = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialFilter = searchParams.get("filter") || "all";
  
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]); // Full list for counting
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all clients for proper counting
  const fetchClients = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      
      // Fetch all clients first for proper counting
      const response = await axios.get(
        `${API_URL}/api/admin/clients?limit=200`,
        { headers: getAuthHeaders() }
      );
      
      setAllClients(response.data.clients || []);
      
      if (showToast) toast.success("Lista actualizada");
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error cargando clientes");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter clients based on active filter and search
  const filteredClients = useMemo(() => {
    let result = [...allClients];
    
    // Apply type/status filter
    switch (activeFilter) {
      case "premium":
        result = result.filter(c => c.is_premium);
        break;
      case "free":
        result = result.filter(c => !c.is_premium);
        break;
      case "pending":
        result = result.filter(c => c.has_pending_checkin);
        break;
      case "inactive":
        result = result.filter(c => !c.is_active && c.latest_checkin);
        break;
      default:
        break;
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      );
    }
    
    // Sort: pending first, then by name
    result.sort((a, b) => {
      if (a.has_pending_checkin !== b.has_pending_checkin) {
        return a.has_pending_checkin ? -1 : 1;
      }
      return (a.name || "").localeCompare(b.name || "");
    });
    
    return result;
  }, [allClients, activeFilter, searchQuery]);

  // Calculate counts for filters
  const counts = useMemo(() => ({
    all: allClients.length,
    premium: allClients.filter(c => c.is_premium).length,
    free: allClients.filter(c => !c.is_premium).length,
    pending: allClients.filter(c => c.has_pending_checkin).length,
    inactive: allClients.filter(c => !c.is_active && c.latest_checkin).length
  }), [allClients]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setSearchParams(filter === "all" ? {} : { filter });
  };

  const handleRefresh = () => fetchClients(true);

  const handleClientClick = (client) => {
    // Navigate to premium clients page with this client selected
    if (client.is_premium) {
      navigate(`/admin/premium-clients?client=${client.id}`);
    } else {
      navigate(`/admin/members?client=${client.id}`);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in pb-8" data-testid="admin-clients-hub">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin")}
          className="text-[#666] hover:text-white"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-heading font-black text-white">Clientes</h1>
          <p className="text-[#666] text-xs">
            {filteredClients.length} de {allClients.length} clientes
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-[#666] hover:text-[#D4AF37]"
        >
          <RefreshCw className={cn("w-5 h-5", refreshing && "animate-spin")} />
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
        <Input
          placeholder="Buscar por nombre o email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-[#121212] border-[#2A2A2A] text-white placeholder:text-[#555] h-11"
          data-testid="search-input"
        />
      </div>

      {/* Filters */}
      <ClientFilters
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        counts={counts}
      />

      {/* Stats Summary */}
      {activeFilter !== "all" && (
        <div className="flex items-center gap-2 py-2">
          {activeFilter === "premium" && (
            <>
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm text-[#D4AF37]">{counts.premium} clientes premium</span>
            </>
          )}
          {activeFilter === "free" && (
            <>
              <Users className="w-4 h-4 text-[#888]" />
              <span className="text-sm text-[#888]">{counts.free} clientes free</span>
            </>
          )}
          {activeFilter === "pending" && (
            <>
              <AlertCircle className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm text-[#D4AF37]">{counts.pending} check-ins pendientes</span>
            </>
          )}
          {activeFilter === "inactive" && (
            <>
              <AlertCircle className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-orange-400">{counts.inactive} clientes inactivos</span>
            </>
          )}
        </div>
      )}

      {/* Clients List */}
      <div className="space-y-2">
        {loading ? (
          // Loading skeletons
          <>
            <ClientSkeleton />
            <ClientSkeleton />
            <ClientSkeleton />
            <ClientSkeleton />
            <ClientSkeleton />
          </>
        ) : filteredClients.length > 0 ? (
          filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={handleClientClick}
            />
          ))
        ) : (
          // Empty state
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="py-12 text-center">
              {searchQuery ? (
                <>
                  <Search className="w-10 h-10 text-[#333] mx-auto mb-3" />
                  <p className="text-[#888]">No se encontraron clientes</p>
                  <p className="text-[#555] text-sm mt-1">Prueba con otro término</p>
                </>
              ) : activeFilter === "pending" ? (
                <>
                  <AlertCircle className="w-10 h-10 text-green-500/30 mx-auto mb-3" />
                  <p className="text-[#888]">¡Todo al día!</p>
                  <p className="text-[#555] text-sm mt-1">No hay check-ins pendientes</p>
                </>
              ) : (
                <>
                  <Users className="w-10 h-10 text-[#333] mx-auto mb-3" />
                  <p className="text-[#888]">No hay clientes</p>
                  <p className="text-[#555] text-sm mt-1">en esta categoría</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Load more hint */}
      {filteredClients.length >= 50 && (
        <p className="text-center text-xs text-[#555] py-2">
          Mostrando primeros 50 resultados. Usa el buscador para filtrar.
        </p>
      )}
    </div>
  );
};

export default AdminClientsHub;
